import {Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, SelectItem} from "@heroui/react";
import {addToast} from "@heroui/toast";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useState} from "react";
import {useGlobalModal} from "../../core/GlobalModal.tsx";
import {handleEntryCapError} from "./EntryCapModal.tsx";
import services from "../../services";
import queryKeys from "../../utilities/queryKeys.ts";
import PButton from "../PButton.tsx";
import PInput from "../PInput.tsx";
import PSelect from "../PSelect.tsx";
import type {DivisionTab} from "../events/DivisionTabs.tsx";

interface CreatePlayerModalProps {
    clubUid?: string;
    leagueUid?: string;
    tournamentUid?: string;
    title?: string;
    /** A tournament's divisions (two or more): the player is put straight into the one picked. */
    divisions?: DivisionTab[];
    /** The division picked to start with, e.g. the one the organiser is looking at. */
    defaultDivision?: string | null;
    onCreated?: (player?: {uid: string, name: string}) => void;
}

const CreatePlayerModal = ({clubUid, leagueUid, tournamentUid, title, divisions = [], defaultDivision = null, onCreated}: CreatePlayerModalProps) => {
    const globalModal = useGlobalModal();
    const queryClient = useQueryClient();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const pickDivision = !!tournamentUid && divisions.length >= 2;
    const [division, setDivision] = useState<string>(defaultDivision && divisions.some((d) => d.key === defaultDivision) ? defaultDivision : '');

    const createMutation = useMutation({
        mutationFn: async () => {
            return services.players.createAndAdd({
                first_name: firstName,
                last_name: lastName || undefined,
                display_name: displayName || undefined,
                email: email || undefined,
                phone: phone || undefined,
                league_uid: leagueUid,
                tournament_uid: tournamentUid,
                ...(pickDivision && division ? {division} : {}),
            });
        },
        onSuccess: (result: any) => {
            if (clubUid) {
                queryClient.invalidateQueries({queryKey: queryKeys.club_applications(clubUid)});
            }
            if (leagueUid) {
                queryClient.invalidateQueries({queryKey: queryKeys.leagues_applications(leagueUid)});
            }
            if (tournamentUid) {
                queryClient.invalidateQueries({queryKey: queryKeys.tournament_applications(tournamentUid)});
                queryClient.invalidateQueries({queryKey: queryKeys.tournament_teams(tournamentUid)});
            }
            addToast({title: 'Player created successfully', color: 'success'});
            const playerName = displayName.trim() || `${firstName.trim()} ${lastName.trim()}`.trim();
            onCreated?.(result?.uid ? {uid: result.uid, name: playerName} : undefined);
            globalModal.close();
        },
        onError: (error: any) => {
            if (handleEntryCapError(globalModal, error, {tournamentUid, leagueUid, onUnlocked: () => onCreated?.()})) return;
            addToast({title: error.message, color: 'danger'});
        },
    });

    const handleSubmit = () => {
        if (!firstName.trim()) {
            addToast({title: 'First name is required', color: 'warning'});
            return;
        }
        if (pickDivision && !division) {
            addToast({title: 'Pick a division', description: 'Each division plays its own draw, so the player needs one.', color: 'warning'});
            return;
        }
        createMutation.mutate();
    };

    return (
        <Modal isOpen onClose={() => globalModal.close()} placement="center">
            <ModalContent>
                <ModalHeader>{title || 'Create Player'}</ModalHeader>
                <ModalBody className="gap-4">
                    <PInput
                        label="First Name"
                        placeholder="Enter first name"
                        value={firstName}
                        onValueChange={setFirstName}
                        isRequired
                        autoFocus
                    />
                    <PInput
                        label="Last Name"
                        placeholder="Enter last name"
                        value={lastName}
                        onValueChange={setLastName}
                    />
                    <PInput
                        label="Display Name"
                        placeholder={firstName.trim() || 'Defaults to first name'}
                        description="Optional. Defaults to first name if left empty."
                        value={displayName}
                        onValueChange={setDisplayName}
                    />
                    {pickDivision && (
                        <PSelect
                            label="Division"
                            placeholder="Pick a division"
                            description="The player goes into this division's draw."
                            isRequired
                            selectedKeys={division ? [division] : []}
                            onSelectionChange={(keys: any) => setDivision(String(keys.currentKey ?? Array.from(keys)[0] ?? ''))}
                        >
                            {divisions.map((d) => <SelectItem key={d.key}>{d.name}</SelectItem>)}
                        </PSelect>
                    )}
                    {/* Phone first — most players live in the organizer's WhatsApp group, not
                        their inbox. The number is stored unverified: only the player entering an
                        OTP themselves (from the share link) verifies it and counts. */}
                    <PInput
                        label="Phone (WhatsApp)"
                        placeholder="+971 50 123 4567"
                        description="With country code. The player verifies it themselves when they sign up from your invite link."
                        type="tel"
                        value={phone}
                        onValueChange={setPhone}
                    />
                    <PInput
                        label="Email"
                        placeholder="Enter email (optional)"
                        description="Optional — also sends the account invite by email."
                        type="email"
                        value={email}
                        onValueChange={setEmail}
                    />
                </ModalBody>
                <ModalFooter>
                    <PButton variant="flat" onPress={() => globalModal.close()}>
                        Cancel
                    </PButton>
                    <PButton color="primary" onPress={handleSubmit} isLoading={createMutation.isPending}>
                        Create Player
                    </PButton>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default CreatePlayerModal;
